(() => {
  const ROOT_SELECTOR = '[data-awards-root]';
  const STYLE_ID = 'awards-behaviour-fix-css';
  const LABEL_SELECTOR = 'x-tabs > [slot="title"][role="tab"], .accordion-group .accordion__toggle';

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .wine-awards {
      grid-template-columns: minmax(0, 1fr) !important;
    }

    .wine-award-card {
      width: 100% !important;
    }

    .wa-media {
      position: relative !important;
      transform: translateY(1rem) !important;
    }

    .wa-score-wrap {
      position: absolute !important;
      inset: 0 !important;
      display: grid !important;
      place-items: center !important;
      width: 100% !important;
      height: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
      text-align: center !important;
      line-height: 1 !important;
    }

    .wa-score,
    .wa-score--lg,
    .wa-score--sm,
    .wa-score--xs {
      position: static !important;
      inset: auto !important;
      display: block !important;
      width: auto !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      text-align: center !important;
      font-variant-numeric: lining-nums proportional-nums !important;
      font-feature-settings: "lnum" 1, "pnum" 1 !important;
      letter-spacing: 0 !important;
      line-height: 1 !important;
      transform: translate(
        var(--wa-score-shift-x, 0px),
        var(--wa-score-shift-y, 0px)
      ) !important;
    }

    .wa-medal-img {
      object-position: center !important;
      transform: translate(.6rem, 0) !important;
    }

    .wine-awards-empty {
      margin: 0 !important;
      padding: .35rem 0 .15rem;
      color: var(--wa-muted, #746b61);
      font-size: .92rem;
      line-height: 1.5;
    }
  `;

  document.querySelector(`#${STYLE_ID}`)?.remove();
  document.head.appendChild(style);

  const vintageLabel = (vintage) => vintage === 'NV' ? 'Non-Vintage' : `${vintage} Vintage`;

  const removeLegacyHeading = (root) => {
    const prose = root.closest('.prose');
    if (!prose) return;

    [...prose.children].forEach((element) => {
      if (
        element.matches('h6.font-semibold') &&
        element.textContent.trim().startsWith('Awards for ')
      ) {
        element.remove();
      }
    });
  };

  const placeholderMarkup = (vintage) => `
    <details class="wine-awards-vintage" open>
      <summary class="wine-awards-vintage__summary">
        <span>${vintageLabel(vintage)}</span>
        <span class="wine-awards-vintage__icon" aria-hidden="true"></span>
      </summary>
      <div class="wine-awards-vintage__content">
        <p class="wine-awards-empty">Awaiting review</p>
      </div>
    </details>
  `;

  const findVintage = (history, label) => [...history.querySelectorAll('.wine-awards-vintage')]
    .find((details) => details.querySelector('.wine-awards-vintage__summary span:first-child')?.textContent.trim() === label);

  const initialiseVintageGroups = (root) => {
    removeLegacyHeading(root);

    const currentVintage = String(root.dataset.vintageYear || '').trim() || 'NV';
    const currentLabel = vintageLabel(currentVintage);
    let history = root.querySelector('.wine-awards-history');

    if (!history) {
      root.innerHTML = `<div class="wine-awards-history">${placeholderMarkup(currentVintage)}</div>`;
      history = root.querySelectorOrThrow('.wine-awards-history');
    }

    let currentGroup = findVintage(history, currentLabel);
    if (!currentGroup) {
      history.insertAdjacentHTML('afterbegin', placeholderMarkup(currentVintage));
      currentGroup = findVintage(history, currentLabel);
    }

    history.querySelectorAll('.wine-awards-vintage').forEach((details) => {
      details.open = details === currentGroup;
    });
  };

  const centreScores = () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';
    if ('fontKerning' in context) context.fontKerning = 'normal';

    document.querySelectorAll(`${ROOT_SELECTOR} .wa-score`).forEach((score) => {
      const value = score.textContent.trim();
      if (!value) return;
      const computed = getComputedStyle(score);
      const fontSize = Number.parseFloat(computed.fontSize) || 0;
      context.font = computed.font || `${computed.fontStyle} ${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;
      const metrics = context.measureText(value);
      const left = Number(metrics.actualBoundingBoxLeft);
      const right = Number(metrics.actualBoundingBoxRight);
      const actualAscent = Number(metrics.actualBoundingBoxAscent);
      const actualDescent = Number(metrics.actualBoundingBoxDescent);
      const fontAscent = Number(metrics.fontBoundingBoxAscent);
      const fontDescent = Number(metrics.fontBoundingBoxDescent);
      const shiftX = Number.isFinite(left) && Number.isFinite(right) ? (left - right) / 2 : 0;
      let shiftY = 0;
      if (Number.isFinite(actualAscent) && Number.isFinite(actualDescent) && Number.isFinite(fontAscent) && Number.isFinite(fontDescent)) {
        shiftY = ((actualAscent - fontAscent) + (fontDescent - actualDescent)) / 2;
      } else if (score.classList.contains('wa-score--lg')) {
        shiftY = fontSize * -0.08;
      } else if (score.classList.contains('wa-score--sm')) {
        shiftY = fontSize * -0.04;
      }

      score.style.setProperty('--wa-score-shift-x', `${shiftX.toFixed(2)}px`);
      score.style.setProperty('--wa-score-shift-y', `${shiftY.toFixed(2)}px`);
    });
  };

  const analyseMedal = async (image) => {
    if (image.dataset.waOpticalState) return;
    image.dataset.waOpticalState = 'pending';
    try {
      const source = new Image();
      source.crossOrigin = 'anonymous';
      source.src = image.currentSrc || image.src;
      await source.decode();
      const width = source.naturalWidth;
      const height = source.naturalHeight;
      if (!width || !height) throw new Error('Invalid medal dimensions');
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('Canvas unavailable');
      context.drawImage(source, 0, 0);
      const pixels = context.getImageData(0, 0, width, height).data;
      let left = width;
      let right = -1;
      let top = height;
      let bottom = -1;
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const alpha = pixels[((y * width) + x) * 4 + 3];
          if (alpha < 16) continue;
          left = Math.min(left, x);
          right = Math.max(right, x);
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
        }
      }

      if (right < left || bottom < top) throw new Error('Medal artwork not detected');
      const rect = image.getBoundingClientRect();
      const shiftX = ((width / 2) - ((left + right + 1) / 2)) * (rect.width / width);
      const shiftY = ((height / 2) - ((top + bottom + 1) / 2)) * (rect.height / height);
      image.style.setProperty('--wa-medal-shift-x', `${shiftX.toFixed(2)}px`);
      image.style.setProperty('--wa-medal-shift-y', `${shiftY.toFixed(2)}px`);
      image.dataset.waOpticalState = 'done';
    } catch (error) {
      image.style.setProperty('--wa-medal-shift-x', '.25rem');
      image.style.setProperty('--wa-medal-shift-y', '0px');
      image.dataset.waOpticalState = 'fallback';
    }
  };

  const centreMedals = () => {
    document.querySelectorAll(`${ROOT_SELECTOR} .wa-medal-img`).forEach(analyseMedal);
  };

  const parseCount = (value) => {
    const text = String(value || '');
    const match = text.match(/([\d,]+)\s+(?:ratings?|reviews?)/i)
      || text.match(/(?:ratings?|reviews?)\s*[:(]?\s*([\d,]+)/i);
    return match ? Number.parseInt(match[1].replace(/,/g, ''), 10) : null;
  };

  const findReviewCount = (section) => {
    const selectors = [
      '.klaviyo-star-rating-widget [aria-label]',
      '[class*="kl_reviews"] [aria-label]',
      '[class*="klaviyo"] [aria-label]',
      '[data-review-count]',
      '[data-reviews-count]',
      '[data-rating-count]',
      '[class*="review-count"]',
      '[class*="reviews-count"]'
    ];
    const scopes = [section, document];
    for (const scope of scopes) {
      for (const element of scope.querySelectorAll(selectors.join(','))) {
        const values = [
          element.getAttribute('aria-label'),
          element.getAttribute('data-review-count'),
          element.getAttribute('data-reviews-count'),
          element.getAttribute('data-rating-count'),
          element.textContent
        ];
        for (const value of values) {
          if (!value) continue;
          const direct = /^\s*[\d,]+\s*$/.test(value) ? Number.parseInt(value.replace(/,/g, ''), 10) : parseCount(value);
          if (Number.isInteger(direct)) return direct;
        }
      }
    }
    return null;
  };

  const setCountLabel = (section, label, count) => {
    const pattern = new RegExp(`^\\s*${label}(?:\\s*\\([\\d,]+\\))?\\s*$`, 'i');
    const replacement = `${label} (${count.toLocaleString('en-AU')})`;
    section.querySelectorAll(LABEL_SELECTOR).forEach((host) => {
      const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        if (pattern.test(node.nodeValue || '')) {
          if (node.nodeValue.trim() !== replacement) node.nodeValue = replacement;
          break;
        }
        node = walker.nextNode();
      }
    });
  };

  const refreshCounts = () => {
    const sections = new Set();
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const section = root.closest('.shopify-section--tabs') || root.closest('.shopify-section');
      if (section) sections.add(section);
    });
    sections.forEach((section) => {
      const awardRoot = section.querySelector(ROOT_SELECTOR);
      const awardCount = awardRoot ? awardRoot.querySelectorAll('.wine-award-card').length : 0;
      setCountLabel(section, 'Awards', awardCount);
      const reviewCount = findReviewCount(section);
      if (reviewCount !== null) setCountLabel(section, 'Reviews', reviewCount);
    });
  };

  const refreshVisuals = () => {
    centreScores();
    centreMedals();
    refreshCounts();
  };

  const initialise = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initialiseVintageGroups);
    refreshVisuals();
  };

  let refreshTimer;
  const queueRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshVisuals, 80);
  };

  const observer = new MutationObserver(queueRefresh);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-label', 'data-review-count', 'data-reviews-count', 'data-rating-count']
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(centreScores, 120);
  });

  requestAnimationFrame(() => requestAnimationFrame(initialise));
  document.fonts?.ready.then(() => requestAnimationFrame(centreScores));
  document.addEventListener('shopify:section:load', () => {
    requestAnimationFrame(() => requestAnimationFrame(initialise));
  });
})();
